/**
 * CR-6322: ASN (call-off) delivery-note column helpers.
 *
 * The available columns + their order are owned by the backend registry
 * (`backend/purchasing/asn_columns.py`) and delivered per ASN as
 * `column_format`. This module only holds the client-side derivation +
 * formatting for each column so the grid renders identically to the print.
 *
 * Keep the column ids in sync with the backend registry.
 */
import type { ASNDeliveryLineDetail } from '../../lib/api';
import { plainDecimal } from '../../lib/plainDecimal';

/**
 * Base tare weight (kg). Mirror of backend `ASN_PALLET_TARE_KG`. The line tare
 * is this value DIVIDED BY the number of pallets on the line (see
 * `asnTareWeight`). Interim until a per-product tare is sourced from SAP.
 */
export const ASN_TARE_KG = 20;

const num = (v: string | number | null | undefined): number => {
  const n = typeof v === 'number' ? v : parseFloat(v ?? '');
  return Number.isFinite(n) ? n : 0;
};

export const asnQty = (line: ASNDeliveryLineDetail): number => num(line.qty_released);
export const asnUnitWeight = (line: ASNDeliveryLineDetail): number => num(line.unit_weight_kg);
export const asnPalletQty = (line: ASNDeliveryLineDetail): number => num(line.pallet_qty);

/** Whole pallets to book — a part-filled pallet still occupies a full space. */
export const asnPalletCount = (line: ASNDeliveryLineDetail): number => {
  const pq = asnPalletQty(line);
  return pq > 0 ? Math.ceil(asnQty(line) / pq) : 0;
};
export const asnLineWeight = (line: ASNDeliveryLineDetail): number => asnQty(line) * asnUnitWeight(line);
// CR-6322: tare = 20 kg DIVIDED BY the number of pallets on the line. No pallets -> no tare.
export const asnTareWeight = (line: ASNDeliveryLineDetail): number => {
  const pallets = asnPalletCount(line);
  return pallets > 0 ? ASN_TARE_KG / pallets : 0;
};
/** The tare as printed — the one weight that is rounded, because it is a constant split across pallets. */
export const asnTareWeightShown = (line: ASNDeliveryLineDetail): number => Number(asnTareWeight(line).toFixed(2));
/**
 * Line weight plus the *printed* tare, so the gross cell always equals its two
 * visible parts. Line weight itself is never rounded (mirror of backend
 * `asn_gross_weight`).
 */
export const asnGrossWeight = (line: ASNDeliveryLineDetail): number => asnLineWeight(line) + asnTareWeightShown(line);

/** CR-7194: price per unit. Mirror of backend `asn_unit_price`. */
export const asnUnitPrice = (line: ASNDeliveryLineDetail): number => num(line.unit_price);
/**
 * CR-7194: price per unit x quantity RELEASED. Mirror of backend
 * `asn_line_total` — deliberately not `CallOffLine.line_total`, which is stored
 * against the requested qty rather than the qty actually being shipped.
 */
export const asnLineTotal = (line: ASNDeliveryLineDetail): number =>
  asnUnitPrice(line) * asnQty(line);
/** True when the line carries a price at all (an unpriced part reads as a dash). */
export const asnHasPrice = (line: ASNDeliveryLineDetail): boolean =>
  line.unit_price != null && line.unit_price !== '';

/** Column ids whose footer total is the sum of the per-line value. */
export const ASN_TOTALLED_COLUMNS = new Set([
  'total_pallets', 'line_weight', 'tare_weight', 'gross_weight',
]);

export const asnColumnTotal = (columnId: string, lines: ASNDeliveryLineDetail[]): string => {
  switch (columnId) {
    case 'total_pallets':
      return String(lines.reduce((s, l) => s + asnPalletCount(l), 0));
    case 'line_weight':
      return plainDecimal(lines.reduce((s, l) => s + asnLineWeight(l), 0));
    case 'tare_weight':
      return lines.reduce((s, l) => s + asnTareWeight(l), 0).toFixed(2);
    case 'gross_weight': {
      // Line total + printed tare total: the footer's gross equals the
      // footer's line weight plus the footer's tare.
      const lineTotal = lines.reduce((s, l) => s + asnLineWeight(l), 0);
      const tareTotal = Number(lines.reduce((s, l) => s + asnTareWeight(l), 0).toFixed(2));
      return plainDecimal(lineTotal + tareTotal);
    }
    default:
      return '';
  }
};

/**
 * Display text for a data column. The grid handles the `attachments` action
 * column separately; everything else routes through here so grid + print agree.
 */
export function asnCellText(
  columnId: string,
  line: ASNDeliveryLineDetail,
  formatDate: (d?: string) => string,
): string {
  switch (columnId) {
    case 'delivery_number':
      return line.delivery_number || '-';
    case 'description':
      return line.part_description || '-';
    case 'part_number':
      return line.part_number || '-';
    case 'commodity_code':
      return line.commodity_code || '-';
    case 'po_line':
      if (!line.po_order_number) return '-';
      return line.po_line_number
        ? `${line.po_order_number} / ${line.po_line_number}`
        : line.po_order_number;
    case 'qty_released':
      return line.qty_released || '-';
    case 'pieces_per_pallet':
      return asnPalletQty(line) > 0 ? String(line.pallet_qty) : '—';
    case 'total_pallets':
      return asnPalletQty(line) > 0 ? String(asnPalletCount(line)) : '—';
    // Weights print exactly as the data carries them (up to 6 decimals, no
    // padding) — only the tare, a Helix constant split across pallets, is
    // rounded, to 2 places.
    case 'net_weight':
      return asnUnitWeight(line) > 0 ? plainDecimal(line.unit_weight_kg) : '—';
    case 'line_weight':
      return asnUnitWeight(line) > 0 ? plainDecimal(asnLineWeight(line)) : '—';
    case 'tare_weight':
      return asnPalletCount(line) > 0 ? asnTareWeight(line).toFixed(2) : '—';
    case 'gross_weight':
      return asnUnitWeight(line) > 0 || asnPalletCount(line) > 0
        ? plainDecimal(asnGrossWeight(line))
        : '—';
    case 'call_off_reference':
      return line.call_off_reference || '-';
    case 'sales_order':
      return line.sales_order || '-';
    case 'delivery_option':
      return line.delivery_option || '-';
    case 'sap_row':
      return line.sap_order_row != null ? String(line.sap_order_row) : '-';
    case 'released_date':
      return formatDate(line.date_released ?? undefined);
    // CR-7194: an unpriced part (no price fed from the ERP yet) reads as a
    // dash; a genuine zero price still shows 0.00.
    case 'unit_price':
      return asnHasPrice(line) ? asnUnitPrice(line).toFixed(2) : '—';
    case 'line_total':
      return asnHasPrice(line) ? asnLineTotal(line).toFixed(2) : '—';
    case 'po_number':
      return line.po_order_number || '-';
    case 'po_line_number':
      return line.po_line_number || '-';
    default:
      return '-';
  }
}
